// src/hooks/useDoctorProfileCompletion.js
import { useMemo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchDoctorInfoByDoctorId } from "../../ProfileDoctor/redux/thunks/doctorProfileThunks";

// ✅ IMPORTANT: on lit EXACTEMENT la même data que useDoctorProfile
import {
  selectDoctorInfo,
  selectDoctorInfoLoading,
} from "../../ProfileDoctor/redux/selectors/doctorProfileSelectors";

/* =========================
   Utils
========================= */
const isEmpty = (v) => {
  if (v === null || v === undefined) return true;

  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return !s || s === "null" || s === "undefined";
  }

  if (Array.isArray(v)) return v.length === 0;

  // ✅ objet vide OU objet rempli mais avec valeurs vides uniquement
  if (typeof v === "object") {
    const keys = Object.keys(v || {});
    if (keys.length === 0) return true;

    // si toutes les valeurs sont vides → considère vide
    return keys.every((k) => isEmpty(v[k]));
  }

  return false;
};

const getByPath = (obj, path) => {
  try {
    return path.split(".").reduce((acc, k) => acc?.[k], obj);
  } catch {
    return undefined;
  }
};

const pickFirstNonEmpty = (obj, paths) => {
  for (const p of paths) {
    const val = getByPath(obj, p);
    if (!isEmpty(val)) return val;
  }
  return undefined;
};

const asArray = (v) => (Array.isArray(v) ? v : []);
const safeStr = (v) => (v == null ? "" : String(v).trim());

/* =========================
   ✅ ID normalization (comme SubMenu)
========================= */
const normId = (v) => {
  if (v == null) return null;
  let s = String(v).trim();
  if (!s) return null;
  if (s.startsWith("DR_") || s.startsWith("dr_")) s = s.slice(3);
  if (s.startsWith("DOC_") || s.startsWith("doc_")) s = s.slice(4);
  return s.trim() || null;
};

const resolveDoctorId = (currentUser) => {
  const u = currentUser || {};
  return (
    normId(u?.id) ||
    normId(u?.doctorId) ||
    normId(u?.userId) ||
    normId(u?._id) ||
    normId(u?.raw?.id) ||
    normId(u?.raw?.doctorId) ||
    normId(u?.raw?.userId) ||
    null
  );
};

const firstClinic = (doctorInfo) => {
  const clinics = doctorInfo?.clinics;
  if (!Array.isArray(clinics) || clinics.length === 0) return null;
  return clinics[0] || null;
};

const extractClinicName = (doctorInfo) => {
  const c0 = firstClinic(doctorInfo);
  if (!c0) return null;

  const name = pickFirstNonEmpty(c0, [
    "name",
    "clinicName",
    "clinic_name",
    "cabinetName",
    "cabinet_name",
    "practiceName",
    "practice_name",
    "title",
  ]);

  return isEmpty(name) ? null : safeStr(name);
};

const extractClinicAddress = (doctorInfo, currentUser) => {
  const c0 = firstClinic(doctorInfo);
  const u = currentUser || {};

  // 1) address object
  if (c0?.address && typeof c0.address === "object") {
    const street = safeStr(c0.address.street);
    const city = safeStr(c0.address.city);
    const country = safeStr(c0.address.country);
    const zip = safeStr(c0.address.zip || c0.address.postalCode || c0.address.codePostal);

    const combined = [street, city, zip, country].filter(Boolean).join(", ");
    if (combined) return combined;
  }

  // 2) direct fields on clinic
  const cityDirect = pickFirstNonEmpty(c0, ["city", "ville", "town"]);
  const streetDirect = pickFirstNonEmpty(c0, ["street", "rue"]);
  const countryDirect = pickFirstNonEmpty(c0, ["country", "pays"]);
  const zipDirect = pickFirstNonEmpty(c0, ["zip", "postalCode", "codePostal"]);

  const combinedDirect = [streetDirect, cityDirect, zipDirect, countryDirect]
    .map(safeStr)
    .filter(Boolean)
    .join(", ");
  if (combinedDirect) return combinedDirect;

  // 3) address string variations
  const addrStr = pickFirstNonEmpty(c0, [
    "address",
    "adresse",
    "clinicAddress",
    "cabinetAddress",
    "officeAddress",
    "practicePlace",
    "addressText",
    "formattedAddress",
    "location.address",
    "location.adresse",
    "location.formattedAddress",
  ]);
  if (!isEmpty(addrStr)) return safeStr(addrStr);

  // 4) fallback user
  const userAddr = pickFirstNonEmpty(u, [
    "address",
    "adresse",
    "profile.address",
    "profile.adresse",
    "raw.address",
    "raw.adresse",
  ]);
  if (!isEmpty(userAddr)) return safeStr(userAddr);

  return null;
};

const extractOfficeHours = (doctorInfo, currentUser) => {
  const c0 = firstClinic(doctorInfo);
  const u = currentUser || {};

  // ✅ tu sauvegardes officeHours dans clinic.officeHours
  const ohStr = pickFirstNonEmpty(c0, ["officeHours", "office_hours", "hours", "workingHours"]);
  if (!isEmpty(ohStr)) return safeStr(ohStr);

  // array in doctorInfo
  const ohArr = doctorInfo?.officeHours;
  if (Array.isArray(ohArr) && ohArr.length > 0) return "__array__";

  const userOh = pickFirstNonEmpty(u, ["officeHours", "workingHours", "profile.officeHours"]);
  if (!isEmpty(userOh)) return safeStr(userOh);

  return null;
};

const normalizeList = (arr, pickKeys) => {
  const out = [];
  for (const it of asArray(arr)) {
    if (it == null) continue;

    if (typeof it === "string") {
      const s = it.trim();
      if (s) out.push(s);
      continue;
    }
    if (typeof it === "object") {
      const v = pickFirstNonEmpty(it, pickKeys);
      const s = safeStr(v);
      if (s) out.push(s);
      continue;
    }
  }
  return out.length ? out : null;
};

const extractLanguages = (doctorInfo, currentUser) => {
  const u = currentUser || {};
  const langs =
    doctorInfo?.languages ??
    doctorInfo?.langages ??
    doctorInfo?.spokenLanguages ??
    u?.languages ??
    u?.spokenLanguages ??
    null;

  if (typeof langs === "string" && !isEmpty(langs)) {
    const cleaned = langs.split(",").map((x) => x.trim()).filter(Boolean);
    return cleaned.length ? cleaned : null;
  }

  return normalizeList(langs, ["name", "label", "code", "lang", "language"]);
};

const extractCertifications = (doctorInfo, currentUser) => {
  const u = currentUser || {};
  const certs =
    doctorInfo?.certifications ??
    doctorInfo?.certifs ??
    u?.certifications ??
    u?.certifs ??
    null;

  return normalizeList(certs, ["title", "name", "label"]);
};

export default function useDoctorProfileCompletion() {
  const dispatch = useDispatch();

  const currentUser = useSelector((s) => s.doctor?.user || null);

  // ✅ même source que DoctorPracticePanel (via useDoctorProfile)
  const doctorInfo = useSelector(selectDoctorInfo);
  const infoLoading = useSelector(selectDoctorInfoLoading);

  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onUpd = () => setTick((t) => t + 1);
    window.addEventListener("doctorProfileUpdated", onUpd);
    return () => window.removeEventListener("doctorProfileUpdated", onUpd);
  }, []);

  // ✅ fetch avec un doctorId normalisé
  useEffect(() => {
    const id = resolveDoctorId(currentUser);
    if (!id) return;
    dispatch(fetchDoctorInfoByDoctorId(id));
  }, [dispatch, currentUser, tick]);

  const computed = useMemo(() => {
    const u = currentUser || {};

    const fullName =
      pickFirstNonEmpty(u, ["fullName", "profile.fullName", "raw.fullName"]) ||
      `${pickFirstNonEmpty(u, [
        "firstName",
        "prenom",
        "first_name",
        "profile.firstName",
        "profile.prenom",
        "raw.firstName",
        "raw.prenom",
      ]) || ""} ${
        pickFirstNonEmpty(u, [
          "lastName",
          "nom",
          "last_name",
          "profile.lastName",
          "profile.nom",
          "raw.lastName",
          "raw.nom",
        ]) || ""
      }`.trim();

    const hasFullName =
      !isEmpty(fullName) && String(fullName).trim().split(/\s+/).length >= 2;

    const clinicName = extractClinicName(doctorInfo);
    const clinicAddress = extractClinicAddress(doctorInfo, u);
    const officeHours = extractOfficeHours(doctorInfo, u);
    const languages = extractLanguages(doctorInfo, u);
    const certifications = extractCertifications(doctorInfo, u);

    const REQUIRED = [
      { key: "firstName", label: "First name" },
      { key: "lastName", label: "Last name" },
      { key: "email", label: "Email", paths: ["email", "profile.email", "raw.email"] },
      {
        key: "phone",
        label: "Phone",
        paths: [
          "phone",
          "phoneNumber",
          "tel",
          "telephone",
          "profile.phone",
          "profile.phoneNumber",
          "profile.tel",
          "raw.phone",
          "raw.phoneNumber",
        ],
      },

      { key: "clinicName", label: "Clinic name" },
      { key: "address", label: "Address" },
      { key: "officeHours", label: "Office hours" },
      { key: "languages", label: "Languages" },
      { key: "certifications", label: "Certifications" },

      {
        key: "avatar",
        label: "Profile picture",
        paths: [
          "avatar",
          "avatarUrl",
          "photo",
          "image",
          "profile.avatar",
          "profile.avatarUrl",
          "profile.photo",
          "raw.avatar",
          "raw.avatarUrl",
          "raw.photo",
        ],
      },
    ];

    const missing = [];

    for (const field of REQUIRED) {
      if ((field.key === "firstName" || field.key === "lastName") && hasFullName) continue;

      if (field.key === "clinicName") {
        if (!isEmpty(clinicName)) continue;
        missing.push(field.label);
        continue;
      }

      if (field.key === "address") {
        if (!isEmpty(clinicAddress)) continue;
        missing.push(field.label);
        continue;
      }

      if (field.key === "officeHours") {
        if (!isEmpty(officeHours)) continue;
        missing.push(field.label);
        continue;
      }

      if (field.key === "languages") {
        if (Array.isArray(languages) && languages.length > 0) continue;
        missing.push(field.label);
        continue;
      }

      if (field.key === "certifications") {
        if (Array.isArray(certifications) && certifications.length > 0) continue;
        missing.push(field.label);
        continue;
      }

      if (field.paths) {
        const val = pickFirstNonEmpty(u, field.paths);
        if (isEmpty(val)) missing.push(field.label);
        continue;
      }
    }

    const total = REQUIRED.length;
    const done = total - missing.length;
    const percent = total === 0 ? 0 : Math.round((done / total) * 100);

    return { percent, missing, done, total };
  }, [currentUser, doctorInfo, tick]);

  return {
    loading: !!infoLoading,
    percent: computed.percent,
    missing: computed.missing,
    done: computed.done,
    total: computed.total,
  };
}
